import React from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { useIsEditor } from '../DocumentView/DocumentContext';
import DocumentElement from '../DocumentView/DocumentElement';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import RepeatableSectionFiller from './RepeatableSectionFiller';

interface Props {
  element: DocumentElementType;
}

const Section: React.FC<Props> = ({ element }) => {
  const data = element.data as {
    label?: string;
    description?: string;
    hidden?: boolean;
    repeat?: boolean;
    maxRepeats?: number;
    repeatItemName?: string;
  };
  const title = data?.label || 'Section';
  const description = data?.description || '';
  const isEditor = useIsEditor();
  const children = element.elements || [];

  // At fill time, a repeatable section delegates to a separate component that
  // renders N instances and threads each one's path through RepeatScopeProvider.
  // In editor mode we always render a single template instance with a badge
  // (the "Repeatable" indicator lives on the SectionDropZone in DocumentEditorView).
  if (!isEditor && data?.repeat) {
    return (
      <RepeatableSectionFiller
        element={element}
        title={title}
        description={description}
        maxRepeats={
          typeof data.maxRepeats === 'number' ? data.maxRepeats : undefined
        }
        repeatItemName={data?.repeatItemName?.trim() || undefined}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label className="text-base font-semibold">{title}</Label>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
        <HiddenElementBanner hidden={data?.hidden} />
        <ArchivedElementBanner archived={element.archived} />
      </div>
      <Separator />
      <div className="space-y-4 pl-4 border-l-2 border-muted">
        {children.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-4">
            No elements in this section
          </p>
        ) : (
          children.map((child) => (
            <DocumentElement key={child.id} element={child} />
          ))
        )}
      </div>
    </div>
  );
};

export default Section;
